// api.js - API service for Foundations Risk Aperture frontend
import apiConfig from '../config/apiConfig';
import { getAuthToken as resolveAuthToken } from './authHeaders';

const API_BASE_URL = apiConfig.getBaseUrl();

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  // Token resolution lives in ONE place (services/authHeaders.ts): persona
  // context → persona token only; else sessionStorage demo token; else the
  // localStorage login token. Raw fetch() callers use authHeaders() from the
  // same module, so both paths send the same credential.
  getAuthToken() {
    return resolveAuthToken();
  }

  // Helper method to make authenticated requests
  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const token = this.getAuthToken();

    const config = {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    };

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Network error' }));
        const err = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        // Keep the server's machine-readable code (e.g. 'assessment_paused') so a page can branch on it.
        err.status = response.status;
        if (typeof errorData.error === 'string') err.code = errorData.error;
        throw err;
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // GET request
  async get(endpoint, params = {}) {
    // Handle the case where baseURL might be empty or relative
    let urlString = `${this.baseURL}${endpoint}`;

    // Ensure we have a valid URL
    if (!urlString.startsWith('http://') && !urlString.startsWith('https://')) {
      // If no protocol, use current window location as base
      const protocol = window.location.protocol;
      const hostname = window.location.hostname;
      // For production, the API is proxied through nginx on the same host
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        urlString = `${protocol}//${hostname}:5001${endpoint}`;
      } else {
        // In production, API is proxied through nginx on same host/port
        urlString = `${protocol}//${hostname}${window.location.port ? ':' + window.location.port : ''}${endpoint}`;
      }
    }

    try {
      const url = new URL(urlString);
      Object.keys(params).forEach(key => url.searchParams.append(key, params[key]));

      return this.request(url.pathname + url.search, {
        method: 'GET',
      });
    } catch (error) {
      console.error('Failed to construct URL:', urlString, error);
      // Fallback to simple string concatenation
      const queryString = Object.keys(params).length > 0
        ? '?' + Object.keys(params).map(key => `${key}=${encodeURIComponent(params[key])}`).join('&')
        : '';
      return this.request(`${endpoint}${queryString}`, {
        method: 'GET',
      });
    }
  }

  // POST request
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // PUT request
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  // DELETE request
  async delete(endpoint) {
    return this.request(endpoint, {
      method: 'DELETE',
    });
  }

  // =====================================================
  // AUTHENTICATION
  // =====================================================
  
  async login(credentials) {
    return this.post('/api/auth/login', credentials);
  }

  async register(userData) {
    return this.post('/api/auth/register', userData);
  }

  async logout() {
    return this.post('/api/auth/logout');
  }

  // =====================================================
  // COMPANIES
  // =====================================================
  
  async getCompanies() {
    return this.get('/api/companies');
  }

  async getCompany(id) {
    return this.get(`/api/companies/${id}`);
  }

  async createCompany(companyData) {
    return this.post('/api/companies', companyData);
  }

  async updateCompany(id, companyData) {
    return this.put(`/api/companies/${id}`, companyData);
  }

  // =====================================================
  // ASSESSMENTS
  // =====================================================
  
  async getAssessments(companyId) {
    return this.get('/api/assessments', { company_id: companyId });
  }

  async getAssessment(sessionId) {
    return this.get(`/api/assessments/${sessionId}`);
  }

  async createAssessment(assessmentData) {
    return this.post('/api/assessments', assessmentData);
  }

  async submitAssessmentResponse(sessionId, responses) {
    return this.post(`/api/assessments/${sessionId}/responses`, { responses });
  }

  // =====================================================
  // FOUNDATIONS ANALYTICS
  // =====================================================
  
  async calculateFoundationsRisk(sessionId, options = {}) {
    return this.post(`/api/foundations/calculate-risk/${sessionId}`, options);
  }

  async get360Analytics(sessionId) {
    return this.get(`/api/foundations/360-analytics/${sessionId}`);
  }

  // Completeness gate: every score endpoint answers 409 until every persona has answered.
  async getAssessmentCompleteness(sessionId) {
    const res = await this.get(`/api/foundations/sessions/${sessionId}/completeness`);
    return res.completeness;
  }

  async getWeightingFactors(sessionId) {
    return this.get(`/api/foundations/weighting-factors/${sessionId}`);
  }

  async getResponseAverages(sessionId) {
    return this.get(`/api/foundations/response-averages/${sessionId}`);
  }

  // =====================================================
  // RECOMMENDATIONS
  // =====================================================
  
  async generateRecommendations(sessionId) {
    return this.post(`/api/recommendations/generate/${sessionId}`);
  }

  async getRecommendations(sessionId) {
    return this.get(`/api/recommendations/${sessionId}`);
  }

  async getRecommendationTemplates() {
    return this.get('/api/recommendations/templates');
  }

  async getOutsidePressures(sessionId) {
    return this.get(`/api/risk-intelligence/outside-pressures/${sessionId}`);
  }

  async updateRecommendationTemplate(category, rangeKey, recommendations) {
    return this.put(`/api/recommendations/templates/${category}/${rangeKey}`, {
      recommendations
    });
  }

  async batchUpdateRecommendations(updates) {
    return this.post('/api/recommendations/templates/batch', { updates });
  }

  async markRecommendationsViewed(sessionId) {
    return this.post(`/api/recommendations/${sessionId}/viewed`);
  }

  async getRecommendationHistory(sessionId) {
    return this.get(`/api/recommendations/${sessionId}/history`);
  }

  // =====================================================
  // POLICY DOCUMENTS
  // =====================================================
  
  async uploadPolicyDocument(formData) {
    return this.request('/api/documents/upload', {
      method: 'POST',
      body: formData,
      headers: {}, // Let browser set Content-Type for FormData
    });
  }

  async getPolicyDocuments(companyId) {
    return this.get('/api/documents', { company_id: companyId });
  }

  async evaluatePolicyDocument(documentId) {
    return this.post(`/api/evaluations/evaluate/${documentId}`);
  }

  async getPolicyEvaluations(companyId) {
    return this.get('/api/evaluations', { company_id: companyId });
  }

  // =====================================================
  // DASHBOARD DATA
  // =====================================================
  
  async getDashboardData(companyId, sessionId = null) {
    // Use the Business Intelligence endpoint to get comprehensive dashboard data with proper risk calculations
    if (sessionId) {
      return this.get(`/api/business-intelligence/${sessionId}`);
    } else {
      // For legacy support, return basic structure if no session
      return {
        success: true,
        data: {
          companyId: companyId,
          sessionId: null,
          message: 'No session data available'
        }
      };
    }
  }

  async getComplianceStatus(companyId) {
    return this.get('/api/compliance/status', { company_id: companyId });
  }

  // =====================================================
  // PERSONA NOTIFICATIONS
  // =====================================================
  
  async setupPersonaContacts(sessionId, personas) {
    return this.post(`/api/notifications/setup-personas/${sessionId}`, { personas });
  }

  // personaType narrows the send to a single persona (the dashboard's
  // per-persona nudge); omit it to invite everyone who has not yet opened
  // their link.
  async sendAssessmentInvitations(sessionId, force = false, personaType = null) {
    return this.post(`/api/notifications/send-persona-invitations/${sessionId}`, {
      force,
      ...(personaType ? { personaType } : {}),
    });
  }

  async notifyNextPersona(sessionId, completedPersona) {
    return this.post(`/api/notifications/notify-next/${sessionId}`, { completedPersona });
  }

  async getAssessmentWorkflow(sessionId) {
    return this.get(`/api/notifications/workflow/${sessionId}`);
  }

  async getNotificationHistory(sessionId) {
    return this.get(`/api/notifications/history/${sessionId}`);
  }

  async sendPersonaReminder(sessionId, persona, customMessage = null) {
    return this.post(`/api/notifications/send-reminder/${sessionId}/${persona}`, { customMessage });
  }

  async testEmailConfiguration(testEmail) {
    return this.post('/api/notifications/test-email', { testEmail });
  }

  // =====================================================
  // CYBER RISK SIGNAL (CRS)
  // =====================================================

  async getCyberRiskSignal(sessionId, refresh = false) {
    return this.get(`/api/crs/${sessionId}`, refresh ? { refresh: 'true' } : {});
  }

  async getCyberRiskSignalSummary(sessionId) {
    return this.get(`/api/crs/${sessionId}/summary`);
  }

  async getCyberRiskSignalHistory(sessionId, limit = 10) {
    return this.get(`/api/crs/${sessionId}/history`, { limit });
  }

  async calculateCyberRiskSignal(sessionId) {
    return this.post(`/api/crs/${sessionId}/calculate`);
  }

  // =====================================================
  // HEALTH CHECK
  // =====================================================

  async healthCheck() {
    return this.get('/health');
  }

  async apiHealthCheck() {
    return this.get('/api/health');
  }
}

// Create and export a singleton instance
const apiService = new ApiService();
export default apiService;